import { useDebounceEffect } from 'ahooks';
import { useCallback, useState } from 'react';

import { ModuleSearchPostsReq, ModuleSupportCenterPost } from '@src/api/generated/data-contracts';
import { postSupportSearch } from '@src/api/generated/Support';

export interface SupportHomeSearchState {
  results: ModuleSupportCenterPost[];
  loading: boolean;
  totalCount: number;
}

export interface UseSupportHomeSearchReturn {
  state: SupportHomeSearchState;
  search: (keyword: string) => Promise<void>;
  reset: () => void;
}

const PAGE_SIZE = 10;

/**
 * SupportHome 页面搜索 hook
 * 只加载前 10 个结果，用于展示预览
 */
function useSupportHomeSearch(keyword: string): UseSupportHomeSearchReturn {
  const [state, setState] = useState<SupportHomeSearchState>({
    results: [],
    loading: false,
    totalCount: 0,
  });

  // 搜索函数 - 只获取前 10 个
  const search = useCallback(async (searchKeyword: string) => {
    if (!searchKeyword.trim()) {
      setState({ results: [], loading: false, totalCount: 0 });
      return;
    }

    setState(prev => ({ ...prev, loading: true }));
    try {
      const searchParams: ModuleSearchPostsReq = {
        keyword: searchKeyword.trim(),
        page: 0,
        pageSize: PAGE_SIZE,
        withContent: true,
        searchType: 'both',
      };

      const response = await postSupportSearch(searchParams);

      if (response.code === 0 && response.data) {
        setState({
          results: response.data.items || [],
          loading: false,
          totalCount: response.data.pageInfo?.totalNumber || 0,
        });
      } else {
        console.error('Search failed:', response.msg);
        setState({ results: [], loading: false, totalCount: 0 });
      }
    } catch (error) {
      console.error('Search error:', error);
      setState({ results: [], loading: false, totalCount: 0 });
    }
  }, []);

  // 重置搜索状态
  const reset = useCallback(() => {
    setState({ results: [], loading: false, totalCount: 0 });
  }, []);

  // 防抖搜索
  useDebounceEffect(
    () => {
      if (keyword) {
        search(keyword);
      } else {
        reset();
      }
    },
    [keyword],
    { wait: 300 },
  );

  return {
    state,
    search,
    reset,
  };
}

export default useSupportHomeSearch;
